import { useState } from 'react';
import { api, money, date } from '../api.js';

/**
 * Напомняне до клиент за неплатените му фактури.
 *
 * Две стъпки нарочно: първо се вижда самото писмо — до кого, кои фактури,
 * колко правят — и чак тогава тръгва. Напомнянето за дълг е разговор с
 * клиент; не бива да излиза от едно щракване по погрешка.
 *
 * Ако вече му е писано, датата стои пред очите: така един и същи човек не
 * получава три писма за седмица от трима служители.
 */
export default function Reminder({ partnerId, onSent }) {
  const [letter, setLetter] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  async function preview() {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      setLetter(await api.get(`/api/reports/debts/reminder/${partnerId}`));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/api/reports/debts/reminder/${partnerId}`, {});
      setLetter(null);
      setDone(res.redirected
        ? `Пробен режим — писмото отиде при нас (${res.to}), не при клиента.`
        : `Изпратено до ${res.to}.`);
      onSent?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (done) return <span className="muted">{done}</span>;

  if (!letter) {
    return (
      <>
        <button className="link" disabled={busy} onClick={preview}>
          Напомни по имейл
        </button>
        {error && <span className="error" style={{ marginLeft: 8 }}>{error}</span>}
      </>
    );
  }

  return (
    <div className="note" style={{ width: '100%', marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <strong>До:</strong> {letter.to ?? '— няма записан имейл —'}
        <span className="muted">{letter.rows.length} фактури · {money(letter.total)}</span>
        {letter.lastReminder && (
          <span className="badge">
            писано на {date(letter.lastReminder.sent_at)}
          </span>
        )}
      </div>

      <div style={{ marginTop: 6 }}><strong>Тема:</strong> {letter.subject}</div>

      <pre style={{
        whiteSpace: 'pre-wrap', margin: '10px 0', padding: 10,
        background: 'var(--surface-2)', border: '1px solid var(--border)',
        borderRadius: 6, fontSize: 12,
      }}
      >
        {letter.text}
      </pre>

      <span className="hint">Към писмото се прикача и таблица с фактурите.</span>

      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
        <button className="primary" disabled={busy || !letter.canSend} onClick={send}>
          Изпрати
        </button>
        <button disabled={busy} onClick={() => setLetter(null)}>Откажи</button>
        {!letter.canSend && (
          <span className="muted">
            {letter.to ? 'пощата не е настроена' : 'няма записан имейл на контрагента'}
          </span>
        )}
      </div>
    </div>
  );
}
